import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { type EnrollmentDTO, startCourse, validateCourseCapacity } from '../../lib/enrollments';
import { masterdataApi, type UnterrichtsformItem, kurseApi, type KursItem } from '../../lib/courses';
import { fetchTrial, type TrialLessonDTO } from '../../lib/trials';
import { api, communicationsApi } from '../../lib/api';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import WizardShell from '../ui/wizard-shell';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge } from '../ui/wizard-layout';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  enrollmentId: number;
  /** Bereits geladene Anmeldung (Termin, Unterrichtsform, Lehrer). Ohne sie lädt der
   *  Wizard nur die Stammdaten von /enrollments/{id} und zeigt leere Felder. */
  enrollment?: EnrollmentDTO | null;
  onComplete?: (courseId: number) => void;
}

interface CourseValidation {
  isValid: boolean;
  currentCount: number;
  maxCount: number;
  warning?: string;
  alternativeForms?: Array<{ id: number; name: string; max_schueler?: number }>;
}

const StartCourseWizardModal: React.FC<Props> = ({
  open,
  onOpenChange,
  enrollmentId,
  enrollment: enrollmentFromParent = null,
  onComplete
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isLoading, setIsLoading] = useState(false);

  // Daten
  const [enrollment, setEnrollment] = useState<EnrollmentDTO | null>(null);
  const [trialData, setTrialData] = useState<TrialLessonDTO | null>(null);
  const [unterrichtsformen, setUnterrichtsformen] = useState<UnterrichtsformItem[]>([]);
  const [selectedForm, setSelectedForm] = useState<UnterrichtsformItem | null>(null);
  const [courseData, setCourseData] = useState<KursItem | null>(null);

  // Validierung
  const [courseValidation, setCourseValidation] = useState<CourseValidation | null>(null);

  // E-Mail je Empfängergruppe: Kunde und Schüler, Lehrkraft (T-0289).
  const [entwuerfe, setEntwuerfe] = useState<Record<string, { senden: boolean; betreff: string; html: string }>>({});

  // Daten beim Öffnen laden
  useEffect(() => {
    if (!open || !enrollmentId) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const enrollmentData =
          enrollmentFromParent
          ?? (await api.get<EnrollmentDTO>(`/api/enrollments/${enrollmentId}`));
        setEnrollment(enrollmentData);

        // Trial-Daten laden
        if (enrollmentData.trialLessonId) {
          const trial = await fetchTrial(enrollmentData.trialLessonId);
          if (trial) {
            setTrialData(trial);
          }
        }

        // Unterrichtsformen laden
        const forms = await masterdataApi.unterrichtsformen();
        setUnterrichtsformen(forms || []);

        // Unterrichtsform finden
        if (enrollmentData.unterrichtsformId) {
          const form = forms.find(f => f.id === enrollmentData.unterrichtsformId);
          if (form) {
            setSelectedForm(form);
          }
        }

        // Falls bestehender Kurs: Kurs-Daten laden
        if (enrollmentData.courseId) {
          try {
            const course = await kurseApi.get(enrollmentData.courseId);
            setCourseData(course);
          } catch (e) {
            console.error('Fehler beim Laden des Kurses:', e);
          }
        }
      } catch (err: any) {
        toast.error('Fehler beim Laden der Daten: ' + (err.message || 'Unbekannter Fehler'));
        onOpenChange(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [open, enrollmentId, enrollmentFromParent, onOpenChange]);

  // Kapazität des bestehenden Kurses prüfen. Ein eigener Schritt „Validierung“ war
  // zu technisch (T-0288); ein Hinweis erscheint nur, wenn der Kurs überbucht wäre.
  useEffect(() => {
    if (!open || !enrollment?.courseId || !enrollment?.startDate) {
      setCourseValidation(null);
      return;
    }
    validateCourseCapacity(Number(enrollment.courseId), enrollment.startDate)
      .then(setCourseValidation)
      .catch(() => setCourseValidation(null));
  }, [open, enrollment?.courseId, enrollment?.startDate]);

  const standortId = useMemo(() => {
    const raw = (trialData as any)?.standort_id ?? (trialData as any)?.standortId ?? null;
    const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
  }, [trialData]);

  const composerGroups: RecipientGroup[] = useMemo(() => {
    const sp = String((enrollment as any)?.subprofileId || '').trim();
    if (!sp) return [];
    const teacherWpUserId = Number.parseInt(String(trialData?.teacherId || enrollment?.teacherId || ''), 10) || 0;
    let wpUserId = 0;
    let subIndex: number | null = null;
    if (sp.includes('-')) {
      const [a, b] = sp.split('-', 2);
      wpUserId = Number.parseInt(a, 10) || 0;
      subIndex = b ? (Number.parseInt(b, 10) || null) : null;
    } else {
      wpUserId = Number.parseInt(sp, 10) || 0;
    }
    if (wpUserId <= 0) return [];
    const weekdayName = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][(enrollment?.weekday || 1) - 1];
    const vars = {
      studentName: trialData?.studentName || 'Schüler',
      teacherName: trialData?.teacherName || 'Lehrer',
      courseName: courseData?.name || (enrollment?.courseId ? 'Bestehender Kurs' : 'Neuer Kurs'),
      startDate: enrollment?.startDate || format(new Date(), 'yyyy-MM-dd'),
      weekdayName,
      startTime: enrollment?.startTime?.slice(0, 5) || '15:00',
    };
    // Kunde (Kontoinhaber) und, wenn es ein eigenes Profil ist, der Schüler — in einem
    // Versand; dieselbe Adresse bekommt die Nachricht nur einmal (T-0289).
    const kundeUndSchueler: RecipientGroup['recipients'] = [{
      id: `person:${wpUserId}:`,
      type: 'person' as const,
      wpUserId,
      subIndex: null,
      name: 'Kunde',
    }];
    if (subIndex && subIndex > 1) {
      kundeUndSchueler.push({
        id: `person:${wpUserId}:${subIndex}`,
        type: 'person' as const,
        wpUserId,
        subIndex,
        name: trialData?.studentName || 'Schüler',
      });
    }
    const gruppen: RecipientGroup[] = [{
      key: 'student',
      label: 'Kunde und Schüler',
      recipients: kundeUndSchueler,
      templateKeys: { email: 'course.started.student' },
      vars,
      defaultSelectedChannels: { email: true, sms: false },
    }];
    if (teacherWpUserId > 0) {
      gruppen.push({
        key: 'teacher',
        label: 'Lehrkraft',
        recipients: [{
          id: `person:${teacherWpUserId}:`,
          type: 'person' as const,
          wpUserId: teacherWpUserId,
          subIndex: null,
          name: trialData?.teacherName || 'Lehrkraft',
        }],
        templateKeys: { email: 'course.started.teacher' },
        vars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }
    return gruppen;
  }, [enrollment, trialData, courseData]);

  // Navigation
  const handleNext = () => {
    if (step < 3) {
      setStep((step + 1) as 1 | 2 | 3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as 1 | 2 | 3);
    }
  };

  // Absenden
  const handleSubmit = async () => {
    if (!enrollment) {
      toast.error('Enrollment-Daten fehlen');
      return;
    }

    setIsLoading(true);
    try {
      const result = await startCourse(enrollmentId, {
        sendEmail: false,
      });
      let communicationFailed = false;
      const locationId = Number((trialData as any)?.standortId || (trialData as any)?.standort_id || 0);
      for (const group of composerGroups) {
        const entwurf = entwuerfe[group.key];
        if (!entwurf?.senden) continue;
        const empfaenger = group.recipients.flatMap((r) =>
          r.type === 'person' ? [{ type: 'person' as const, wpUserId: r.wpUserId, subIndex: r.subIndex ?? null }] : []
        );
        if (empfaenger.length === 0 || locationId <= 0) {
          communicationFailed = true;
          continue;
        }
        try {
          const response = await communicationsApi.send({
            channel: 'email',
            standortId: locationId,
            recipients: empfaenger,
            draft: { subjectTpl: entwurf.betreff, htmlTpl: entwurf.html },
            vars: group.vars,
          });
          communicationFailed = communicationFailed || !response.ok;
        } catch {
          communicationFailed = true;
        }
      }
      if (communicationFailed) toast.warning('Kurs gestartet; E-Mail-Versand ist fehlgeschlagen');
      else toast.success('Kurs erfolgreich gestartet');
      onOpenChange(false);
      // Auch ohne neue Kurs-ID neu laden — sonst blieb der Eintrag stehen (T-0290).
      onComplete?.(Number(result.courseId) || 0);
    } catch (error: any) {
      toast.error(error?.message || 'Fehler beim Starten des Kurses');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset beim Schließen
  useEffect(() => {
    if (!open) {
      setStep(1);
      setEnrollment(null);
      setTrialData(null);
      setSelectedForm(null);
      setCourseData(null);
      setCourseValidation(null);
      setEntwuerfe({});
    }
  }, [open]);

  if (!open) return null;

  const weekdayName = enrollment ? ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][(enrollment.weekday || 1) - 1] : '';
  const useExistingCourse = enrollment?.courseId ? true : false;

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Kurs starten"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[
        { label: 'Zusammenfassung' },
        { label: 'E-Mail' },
        { label: 'Übersicht' },
      ]}
      onBack={step > 1 ? handleBack : undefined}
      canBack={step > 1 && !isLoading}
      backLabel="Zurück"
      onNext={step < 3 ? handleNext : undefined}
      canNext={!isLoading && !!enrollment}
      nextLabel="Weiter"
      onPrimary={step === 3 ? handleSubmit : undefined}
      primaryLabel={isLoading ? 'Wird gestartet...' : 'Kurs starten'}
      canPrimary={step === 3 && !isLoading && !!enrollment}
      isLoading={isLoading}
    >
      <div className="py-4 space-y-4">
        {isLoading && !enrollment && (
          <div className="text-center py-8">Daten werden geladen...</div>
        )}

        {enrollment && (
          <>
            {step === 1 && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Kursdetails</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="font-semibold">Kurs:</span>{' '}
                        {useExistingCourse ? (
                          courseData ? (
                            `${courseData.name} (ID: ${enrollment.courseId})`
                          ) : (
                            `Bestehender Kurs (ID: ${enrollment.courseId})`
                          )
                        ) : (
                          'Neuer Kurs wird erstellt'
                        )}
                      </div>
                      <div>
                        <span className="font-semibold">Unterrichtsform:</span>{' '}
                        {selectedForm ? selectedForm.name : enrollment.unterrichtsformId ? `ID: ${enrollment.unterrichtsformId}` : 'Nicht festgelegt'}
                      </div>
                      <div>
                        <span className="font-semibold">Startdatum:</span>{' '}
                        {enrollment.startDate ? format(parseISO(enrollment.startDate), 'dd.MM.yyyy', { locale: de }) : '-'}
                      </div>
                      <div>
                        <span className="font-semibold">Wochentag:</span> {weekdayName}
                      </div>
                      <div>
                        <span className="font-semibold">Uhrzeit:</span>{' '}
                        {enrollment.startTime ? enrollment.startTime.slice(0, 5) : '-'}
                      </div>
                      <div>
                        <span className="font-semibold">Turnus:</span>{' '}
                        {enrollment.intervalWeeks === 1 ? 'Wöchentlich' : `${enrollment.intervalWeeks}-tägig`}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {courseValidation && !courseValidation.isValid ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Der Kurs wäre überbucht</AlertTitle>
                    <AlertDescription>
                      {courseValidation.warning
                        || `Mit dieser Anmeldung wären es ${courseValidation.currentCount + 1} Teilnehmer, erlaubt sind ${courseValidation.maxCount}.`}
                      {courseValidation.alternativeForms && courseValidation.alternativeForms.length > 0
                        ? ` Passende Unterrichtsformen: ${courseValidation.alternativeForms.map((f) => f.name).join(', ')}.`
                        : ''}
                    </AlertDescription>
                  </Alert>
                ) : null}

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Teilnehmer</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      <span className="font-semibold">Schüler:</span> {trialData?.studentName || enrollment.subprofileId}
                    </div>
                    <div>
                      <span className="font-semibold">Lehrer:</span> {trialData?.teacherName || `ID: ${enrollment.teacherId}`}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Schritt 2: E-Mail */}
            {step === 2 && composerGroups.length > 0 && (
              <CommunicationComposer
                active={open && step === 2}
                standortId={standortId}
                groups={composerGroups}
                onDraftsChange={({ groups: g }) => {
                  setEntwuerfe(
                    Object.fromEntries(
                      Object.entries(g).map(([key, entwurf]) => [
                        key,
                        {
                          senden: !!entwurf.selectedChannels.email,
                          betreff: entwurf.email.subjectTpl || '',
                          html: entwurf.email.htmlTpl || '',
                        },
                      ])
                    )
                  );
                }}
              />
            )}

            {/* Schritt 3: Übersicht */}
            {step === 3 && (
              <WizardCard>
                <WizardSection title="Kursdetails">
                  <WizardKVGrid>
                    <WizardKVItem label="Kurs">
                      {useExistingCourse
                        ? (courseData ? courseData.name : `Bestehender Kurs (ID: ${enrollment.courseId})`)
                        : 'Neuer Kurs wird erstellt'}
                    </WizardKVItem>
                    <WizardKVItem label="Unterrichtsform">{selectedForm?.name || '—'}</WizardKVItem>
                    <WizardKVItem label="Startdatum">
                      {enrollment.startDate ? format(parseISO(enrollment.startDate), 'dd.MM.yyyy', { locale: de }) : '—'}
                    </WizardKVItem>
                    <WizardKVItem label="Wochentag">{weekdayName}</WizardKVItem>
                    <WizardKVItem label="Uhrzeit">{enrollment.startTime ? enrollment.startTime.slice(0, 5) : '—'}</WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>
                <WizardSection title="Teilnehmer">
                  <WizardKVGrid>
                    <WizardKVItem label="Schüler">{trialData?.studentName || String(enrollment.subprofileId)}</WizardKVItem>
                    <WizardKVItem label="Lehrer">{trialData?.teacherName || `ID: ${enrollment.teacherId}`}</WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>
                <WizardSection title="Benachrichtigungen">
                  <WizardKVGrid>
                    <WizardKVItem label="E-Mail an Kunde und Schüler"><WizardYesNoBadge yes={!!entwuerfe.student?.senden} /></WizardKVItem>
                    {composerGroups.some((g) => g.key === 'teacher') ? (
                      <WizardKVItem label="E-Mail an die Lehrkraft"><WizardYesNoBadge yes={!!entwuerfe.teacher?.senden} /></WizardKVItem>
                    ) : null}
                  </WizardKVGrid>
                </WizardSection>
              </WizardCard>
            )}
          </>
        )}
      </div>
    </WizardShell>
  );
};

export default StartCourseWizardModal;
